import { useState } from 'react';
import type { Workflow } from '../types';
import { Icon } from './Icon';
import { WorkflowListMenu } from './WorkflowListMenu';

interface Props {
  workflow: Workflow;
  onRename: (name: string) => void;
  onToggleActive: () => void;
  onSave: () => void;
  onTestRun: () => void;
  onClear: () => void;
  onExport: () => void;
  onImport: () => void;
  onTutorial: () => void;
  onTogglePalette: () => void;
  onToggleProps: () => void;
  onSwitchWorkflow: (wf: Workflow) => void;
  onNewWorkflow: () => void;
  onDeleteWorkflow: (id: string) => void;
  paletteOpen: boolean;
  propsOpen: boolean;
  dirty: boolean;
  saving: boolean;
  lastSaved: number | null;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
}

function timeAgo(ts: number | null): string {
  if (!ts) return 'never';
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function Toolbar({
  workflow,
  onRename,
  onToggleActive,
  onSave,
  onTestRun,
  onClear,
  onExport,
  onImport,
  onTutorial,
  onTogglePalette,
  onToggleProps,
  onSwitchWorkflow,
  onNewWorkflow,
  onDeleteWorkflow,
  paletteOpen,
  propsOpen,
  dirty,
  saving,
  lastSaved,
  theme,
  onToggleTheme,
}: Props) {
  const [name, setName] = useState(workflow.name);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  // Keep local name in sync if workflow name changes externally
  if (name !== workflow.name && !document.activeElement?.matches('.wf-name input')) {
    setName(workflow.name);
  }

  return (
    <header className="toolbar" data-tour="toolbar">
      <div className="toolbar-left">
        <button
          className={`sidebar-toggle ${paletteOpen ? 'on' : ''}`}
          onClick={onTogglePalette}
          title={paletteOpen ? 'Hide palette (left)' : 'Show palette (left)'}
          aria-label={paletteOpen ? 'Collapse palette' : 'Expand palette'}
        >
          {paletteOpen ? '⟨' : '⟩'}
        </button>
        <div className="brand">
          <span className="brand-mark">
            <img
              src="https://jalenward.com/images/proposals/impactiq-white-400.png"
              alt="IQ"
              onError={e => {
                const t = e.target as HTMLImageElement;
                t.style.display = 'none';
                t.parentElement!.innerHTML = '<span class="brand-fallback">IQ</span>';
              }}
            />
          </span>
          <span className="brand-text">
            <strong>Workflow Builder</strong>
            <span>Impact IQ · No-code</span>
          </span>
        </div>
        <div className="divider" />
        <div className="wf-name">
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={() => onRename(name.trim() || 'Untitled workflow')}
            onKeyDown={e => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            spellCheck={false}
          />
          <button
            className={`wf-list-toggle ${listOpen ? 'open' : ''}`}
            onClick={() => setListOpen(o => !o)}
            title="Switch workflows"
            aria-label="Open workflow list"
            aria-haspopup="menu"
            aria-expanded={listOpen}
          >
            <Icon name="chevronDown" size={11} variant="silver" />
          </button>
          <span className="wf-status">
            v{workflow.version} ·{' '}
            {saving
              ? <span className="wf-save-spin"><span className="wf-save-dot" /> saving</span>
              : dirty
                ? <span style={{ color: 'var(--signal-amber)' }}>unsaved</span>
                : <span>saved {timeAgo(lastSaved)}</span>
            }
          </span>
          <WorkflowListMenu
            open={listOpen}
            currentId={workflow.id}
            onClose={() => setListOpen(false)}
            onSelect={onSwitchWorkflow}
            onCreate={onNewWorkflow}
            onDelete={onDeleteWorkflow}
          />
        </div>
      </div>

      <div className="toolbar-right">
        <button
          className="theme-toggle"
          onClick={onToggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} variant={theme === 'dark' ? 'gold' : 'silver'} />
        </button>
        <button className="toolbar-btn icon-only" onClick={onTutorial} title="Tutorial · ?" aria-label="Tutorial">?</button>
        <div className="overflow-wrap">
          <button
            className="toolbar-btn icon-only"
            onClick={() => setOverflowOpen(o => !o)}
            title="More actions"
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
          >⋯</button>
          {overflowOpen && (
            <>
              <div className="overflow-backdrop" onClick={() => setOverflowOpen(false)} />
              <div className="overflow-menu" role="menu">
                <button onClick={() => { onImport(); setOverflowOpen(false); }} role="menuitem">⤓ Import workflow JSON</button>
                <button onClick={() => { onExport(); setOverflowOpen(false); }} role="menuitem">⤒ Export workflow JSON</button>
                <button onClick={() => { onClear(); setOverflowOpen(false); }} className="danger" role="menuitem">🗑 Clear canvas</button>
                <button onClick={() => { onTutorial(); setOverflowOpen(false); }} role="menuitem">? Replay tutorial</button>
              </div>
            </>
          )}
        </div>
        <button className="toolbar-btn" onClick={onTestRun} data-tour="test-run">Test run</button>
        <button className="toolbar-btn primary" onClick={onSave} data-tour="save">{dirty ? 'Save' : 'Saved'}</button>
        <div
          className={`active-toggle ${workflow.active ? 'on' : ''}`}
          onClick={onToggleActive}
          data-tour="active-toggle"
          role="switch"
          aria-checked={workflow.active}
          tabIndex={0}
          onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') onToggleActive(); }}
        >
          <span className="switch" />
          <b>{workflow.active ? 'Active' : 'Paused'}</b>
        </div>
        <button
          className={`sidebar-toggle ${propsOpen ? 'on' : ''}`}
          onClick={onToggleProps}
          title={propsOpen ? 'Hide properties (right)' : 'Show properties (right)'}
          aria-label={propsOpen ? 'Collapse properties' : 'Expand properties'}
        >
          {propsOpen ? '⟩' : '⟨'}
        </button>
      </div>
    </header>
  );
}
